import MyFlow from "flow/MyFlow";
import config from "flow/config";
import flowMetaParser from "flow/manifests/flowMetaParser";
import * as webModuleLoader from "flow/init/webModuleLoader";
import ProductEntry from "flow/manifests/ProductEntry";
import ProductConfigManifest from "flow/manifests/ProductConfigManifest";
import ProductConfigManifestEx from "flow/manifests/ProductConfigManifestEx";
import { IProductItem, ProductSelection, Dictionary } from "flow/api/types";
import baseUrl from "flow/wrappers/baseUrl";
import WebModuleEntry from "flow/manifests/WebModuleEntry";
import $ from "jquery";
import service from "flow/data/service";
import environment from "flow/configuration/environment";
import queryHelper from "flow/init/queryHelper";
import * as splashStore from "flow/splash/splashStore";
import { SelectedProduct } from "flow/entities/SelectedProduct";
import { IProductSelectorDialogViewContext } from "flow/viewModels/ProductSelectorDialog";
import moduleLoader from "flow/wrappers/moduleLoader";
import metaCache from "flow/utils/metaCache";
import manifestManager from "flow/utils/manifestManager";
import {
    getProductSelectionFromSessionStorage,
    hasLastProductStoredInSessionStorage,
    saveProductSelectionToSessionStorage
} from "flow/product/productSelectionStorage";
import * as homeConfiguration from "flow/stores/homeConfiguration";
import { IProductEntry } from "flow/manifests/IProductEntry";
import { removeFlow3SuffixFromProductId } from "flow/manifests/productEntryUtils";
import flow3NavigationHelper from "flow/utils/flow3NavigationHelper";

export default class ProductSelector {
    private readonly allWebModuleEntries: WebModuleEntry[];
    private readonly productEntries: IProductEntry[];
    private gotInitialSelectionFromUserSettings: boolean = false;

    constructor(private readonly myFlow: MyFlow) {
        this.allWebModuleEntries = flowMetaParser.getWebModules(this.myFlow);
        this.productEntries = this.getProducts(this.allWebModuleEntries);
    }

    select = async (): Promise<boolean> => {
        if (this.hasForceSingleModule()) {
            // Special case, skipping normal product path
            return this.selectSingleWebModule().then(() => true);
        }

        let selectedProduct: ProductSelection;

        // Check url for selected product
        selectedProduct = this.getSelectedProductFromUrl();

        // Check session storage for selected product
        if (!selectedProduct) {
            selectedProduct = getProductSelectionFromSessionStorage(this.myFlow);
        }

        if (!selectedProduct) {
            selectedProduct = this.getSelectedProductFromUserSettings();
            this.gotInitialSelectionFromUserSettings = !!selectedProduct;
        }

        // If we got anything from those, make sure it is valid and get additional information
        if (selectedProduct) {
            selectedProduct = await this.validateAndSanitizeSelection(selectedProduct);
        }

        // We will possibly loop dialog until the user chooses a valid setup
        // Should normally only be 0 or 1 times.
        while (!selectedProduct?.valid) {
            selectedProduct = await this.askUserToSelectProduct(selectedProduct, false, false);

            if (!selectedProduct) {
                // Default to other
                selectedProduct = {
                    id: config.otherProductId,
                    remember: false,
                    external: false,
                    valid: true
                };
            } else {
                selectedProduct = await this.validateAndSanitizeSelection(selectedProduct);
            }
        }

        if (jumpToFlow2ProductIfNeeded(selectedProduct)) {
            return Promise.resolve(false);
        }

        // We got here, so we have a valid selection

        this.saveSelection(selectedProduct);

        this.setupAjaxHeaderIfNeeded(selectedProduct);

        const selectedWebModuleEntries = this.allWebModuleEntries.filter(e => e.products.includes(selectedProduct.id));

        this.addSystemInformationWebModuleIfRequired(selectedWebModuleEntries);

        if (environment.get<boolean>(environment.keys.homeConfiguration)) {
            return this.myFlow
                .when(
                    homeConfiguration.loadFromServer(this.myFlow, selectedProduct.id),
                    webModuleLoader.load(this.myFlow, selectedWebModuleEntries)
                )
                .then(() => true);
        } else {
            return webModuleLoader.load(this.myFlow, selectedWebModuleEntries).then(() => true);
        }
    };

    private hasForceSingleModule = () => {
        const webModuleId = queryHelper.getSingleModule() || environment.get(environment.keys.singleModule);

        if (!webModuleId) {
            return false;
        }

        const exists = this.allWebModuleEntries.find(wm => wm.id === webModuleId);

        if (!exists) {
            this.myFlow.warn(
                `Force single web module selected, but a module with id ${webModuleId} does not exist. Will ask user to select.`
            );
            environment.set(environment.keys.singleModule, undefined);
            return false;
        }

        environment.set(environment.keys.singleModule, webModuleId);
        return true;
    };

    private selectSingleWebModule = () => {
        const webModuleId = environment.get<string>(environment.keys.singleModule);

        const selectedProduct: ProductSelection = {
            id: config.singleWebModuleProductId,
            remember: false,
            hideChange: hasLastProductStoredInSessionStorage(),
            external: false
        };

        environment.set(environment.keys.singleModule, webModuleId);

        this.saveSelection(selectedProduct);

        const selectedWebModuleEntries = [this.allWebModuleEntries.find(wm => wm.id === webModuleId)];

        this.addSystemInformationWebModuleIfRequired(selectedWebModuleEntries);

        manifestManager.forceSingleModule(webModuleId);

        return webModuleLoader.load(this.myFlow, selectedWebModuleEntries);
    };

    private addSystemInformationWebModuleIfRequired = (webModulesToBeLoaded: WebModuleEntry[]) => {
        const includeSystemInformation = environment.get(environment.keys.systemInformation);

        const systemInfoWebModuleEntry = this.allWebModuleEntries.find(wm => wm.id === config.systemInfoModuleId);
        if (
            includeSystemInformation &&
            systemInfoWebModuleEntry &&
            systemInfoWebModuleEntry.isActive &&
            !webModulesToBeLoaded.includes(systemInfoWebModuleEntry)
        ) {
            webModulesToBeLoaded.push(systemInfoWebModuleEntry);
        }
    };

    private getProducts = (allWebModuleEntries: WebModuleEntry[]): IProductEntry[] => {
        const productItems = flowMetaParser.getProducts(this.myFlow);

        // Add Other product if needed
        if (
            allWebModuleEntries.some(
                w => w.id !== config.systemInfoModuleId && w.products.includes(config.otherProductId)
            )
        ) {
            productItems.push(this.createDefaultProduct());
        }

        if (environment.get(environment.keys.crossLaunch)) {
            const flow3Products = flowMetaParser.getProductsFlow3(this.myFlow);
            productItems.push(...flow3Products);
        }

        return productItems;
    };

    private createDefaultProduct = (): IProductEntry => {
        return new ProductEntry({
            Id: config.otherProductId,
            Name: this.myFlow.translate("FlowHtml.Translations.Other"),
            IsActive: true,
            VisibleInRuntimeContext: environment.get(environment.keys.runtimeContext),
            ConfigurationProvider: undefined
        });
    };

    private createDefaultProductItem = (): IProductItem => {
        const productEntry = this.createDefaultProduct();
        return {
            id: productEntry.id,
            name: productEntry.name,
            broken: false,
            manifest: new ProductConfigManifestEx({ hide: false } as any, config.otherProductId, undefined),
            inactiveReason: undefined,
            external: productEntry.external
        };
    };

    private getSelectedProductFromUrl = (): ProductSelection => {
        try {
            const { productId: productId, payload: payload } = queryHelper.getProductConfigParams();

            if (productId) {
                const product = this.productEntries.find(p => p.id === productId);

                if (!product) {
                    this.myFlow.warn(
                        "Found selected product in url, but no matching product entry. Will ask user to select."
                    );
                    return undefined;
                }

                return {
                    id: productId,
                    payload: payload,
                    remember: false,
                    external: product.external
                };
            }
        } catch (e) {
            this.myFlow.warn(
                "Found selected product in url, but payload was not parsable. Will ask user to select.",
                e
            );
        }

        return undefined;
    };

    private getSelectedProductFromUserSettings(): ProductSelection {
        const id = environment.get<string>(environment.keys.productIdRemember);
        const payloadJSON = environment.get<string>(environment.keys.productPayloadRemember);
        return this.createProductSelection(id, payloadJSON, false);
    }

    private getSelectedProductFromEnvironment(): ProductSelection {
        const id = environment.get<string>(environment.keys.productId);
        const payloadJSON = environment.get<string>(environment.keys.productPayload);
        return this.createProductSelection(id, payloadJSON, false);
    }

    private createProductSelection(id: string, payloadJSON: string, external: boolean): ProductSelection {
        if (!id) {
            return undefined;
        }

        let payload: Dictionary<any> = undefined;

        if (payloadJSON) {
            try {
                payload = JSON.parse(payloadJSON);
            } catch (e) {
                this.myFlow.warn("Failed to parse payload", e);
                return undefined;
            }
        }

        const selectedProduct: ProductSelection = {
            id,
            payload,
            remember: false,
            external
        };

        return selectedProduct;
    }

    private validateAndSanitizeSelection = async (selectedProduct: ProductSelection): Promise<ProductSelection> => {
        if (selectedProduct.id) {
            if (selectedProduct.id === config.otherProductId) {
                // Should already be valid, but just in case
                selectedProduct.valid = true;
                return selectedProduct;
            }

            const product = this.productEntries.find(p => p.id === selectedProduct.id);

            if (product) {
                // Check for Flow 3 version of product if feature enabled
                if (environment.get<boolean>(environment.keys.crossLaunch) && selectedProduct.external) {
                    // Assume it is valid. We will jump to Flow 3 and validate there later on.
                    selectedProduct.valid = true;
                    selectedProduct.remember = false; // Don't allow remembering Flow 3 products in Flow 2
                    return selectedProduct;
                }

                environment.set(environment.keys.productName, product.name);

                if (product.configurationProvider) {
                    splashStore.reset(splashStore.SplashMode.Verifying);

                    try {
                        splashStore.addEntry({ id: product.id, name: product.name });
                        const sanitizedSelection = await service.validateAndSanitize(
                            this.myFlow,
                            product.configurationProvider,
                            product.id,
                            selectedProduct.payload
                        );
                        if (!sanitizedSelection) {
                            throw new Error("Stored selection failed validation and sanitizing.");
                        }

                        selectedProduct.name = sanitizedSelection.name;
                        selectedProduct.description = sanitizedSelection.description;
                        selectedProduct.impersonating = sanitizedSelection.impersonation;
                        selectedProduct.hideChange = sanitizedSelection.hideChange;

                        if (sanitizedSelection.payload) {
                            selectedProduct.payload = JSON.parse(sanitizedSelection.payload);
                        }

                        selectedProduct.valid = true;

                        splashStore.setDone(product.id);

                        return selectedProduct;
                    } catch (e) {
                        this.myFlow.warn("Validation/sanitizing of selection failed. Will ask user for input.");
                        splashStore.setFailed(product.id);
                        selectedProduct.payload = undefined;
                        selectedProduct.valid = false;
                        return selectedProduct;
                    }
                } else if (selectedProduct.payload) {
                    // Unvalidated payload, force user to get rid of it by making them select from dialog
                    this.myFlow.warn("Selection has payload but product has no configuration provider.");
                    selectedProduct.payload = undefined;
                    selectedProduct.valid = false;
                    return selectedProduct;
                } else {
                    selectedProduct.valid = true;
                    return selectedProduct;
                }
            }
        }

        this.myFlow.warn("No matching product for selection id " + selectedProduct.id);
        return undefined;
    };

    private askUserToSelectProduct = async (
        defaultSelection: ProductSelection,
        showCancel: boolean,
        forceDialog: boolean
    ): Promise<ProductSelection> => {
        splashStore.reset(splashStore.SplashMode.Products);
        // Read config from all products that have a configuration provider
        const promises = this.productEntries.map(this.readProductConfiguration);

        const results = await Promise.all(promises);

        // Exclude empty and broken products
        const selectableProducts: IProductItem[] = results.filter(result => result && !result.broken);

        // If default 'Other' product wasn't added from the start,
        // but some products were broken so that we have no left,
        // then we add it here instead.
        if (selectableProducts.length === 0) {
            selectableProducts.push(this.createDefaultProductItem());
        }

        // Show dialog if has more than one, or if the one that we have needs extra data
        if (
            selectableProducts.length > 1 ||
            (selectableProducts[0].manifest && selectableProducts[0].manifest.customOptionsComponent) ||
            (selectableProducts[0].manifest && selectableProducts[0].manifest.inactiveReason) ||
            forceDialog
        ) {
            const viewContext: IProductSelectorDialogViewContext = {
                products: selectableProducts,
                showCancel: showCancel,
                defaultSelection: defaultSelection,
                fromUserSettings: this.gotInitialSelectionFromUserSettings,
                reselect: showCancel
            };

            return await this.myFlow.showCustomDialog<ProductSelection>(
                config.componentIds.productSelectorDialog,
                viewContext
            );
        }

        // Only one choice and it was not because some other were broken
        // Also the chosen one has no customization
        if (!results.some(result => result && result.broken) || !environment.get(environment.keys.useProductSelector)) {
            // Then we don't show change product link
            environment.set(environment.keys.showProductSelector, false);
        }

        return {
            id: selectableProducts[0].id,
            remember: false,
            external: selectableProducts[0].external
        };
    };

    private setupAjaxHeaderIfNeeded = (selectedProduct: ProductSelection) => {
        if (selectedProduct.payload) {
            $(document as any).ajaxSend((_e, xhr, settings) => {
                // Send authorization on all calls except to:
                // 1. Flow itself
                // 2. Product config related calls
                if (
                    !settings.url.includes(baseUrl) &&
                    !settings.url.includes(config.partialUrls.getProductConfigManifest) &&
                    !settings.url.includes(config.partialUrls.validateAndSanitize)
                ) {
                    xhr.setRequestHeader(
                        "AuthorizationContext",
                        encodeURIComponent(JSON.stringify(selectedProduct.payload))
                    );
                }
            });
        }
    };

    private readProductConfiguration = (productEntry: ProductEntry): Promise<IProductItem> => {
        splashStore.addEntry({ id: productEntry.id, name: productEntry.name });
        return new Promise(resolve => {
            if (productEntry.configurationProvider && !productEntry.configurationProvider.endsWith("/")) {
                productEntry.configurationProvider += "/";
            }

            if (environment.get<boolean>(environment.keys.crossLaunch) && productEntry.external) {
                if (productEntry.configurationProvider) {
                    service
                        .getProductConfigManifestFlow3(this.myFlow, productEntry.configurationProvider, productEntry.id)
                        .then((config?: ProductConfigManifest) => {
                            splashStore.setDone(productEntry.id);

                            // We are basically only interested in hide and inactiveReason for Flow 3 products
                            if (config?.hide ?? true) {
                                resolve(undefined);
                                return;
                            }

                            resolve({
                                id: productEntry.id,
                                name: productEntry.name,
                                manifest: new ProductConfigManifestEx(
                                    { hide: false } as any,
                                    productEntry.id,
                                    productEntry.configurationProvider
                                ), // We will load the manifest in Flow 2, so we don't need it here
                                broken: false,
                                inactiveReason: config?.inactiveReason,
                                external: productEntry.external
                            });
                        })
                        .catch((error: any) => {
                            this.myFlow.error("Failed to read product: " + productEntry.id, error);
                            splashStore.setFailed(productEntry.id);
                            resolve({
                                id: productEntry.id,
                                name: productEntry.name,
                                manifest: undefined,
                                broken: true,
                                inactiveReason: undefined,
                                external: productEntry.external
                            });
                        });
                    return;
                }
            }

            if (!productEntry.configurationProvider) {
                splashStore.setDone(productEntry.id);
                resolve({
                    id: productEntry.id,
                    name: productEntry.name,
                    manifest: undefined,
                    broken: false,
                    inactiveReason: undefined,
                    external: productEntry.external
                });
                return;
            }

            service
                .getProductConfigManifest(this.myFlow, productEntry.configurationProvider, productEntry.id)
                .then((config: ProductConfigManifest) => {
                    splashStore.setDone(productEntry.id);

                    const manifest = new ProductConfigManifestEx(
                        config,
                        productEntry.id,
                        productEntry.configurationProvider
                    );

                    if (manifest.hide) {
                        resolve(undefined);
                        return;
                    }

                    // Sanitizing
                    this.fixManifestPaths(manifest, productEntry);

                    this.setCacheBusters(manifest, config.cacheBuster);

                    if (config.metaConfig) {
                        metaCache.add(config.metaConfig);
                    }

                    resolve({
                        id: productEntry.id,
                        name: productEntry.name,
                        manifest: manifest,
                        broken: false,
                        inactiveReason: config.inactiveReason,
                        external: productEntry.external
                    });
                })
                .catch((error: any) => {
                    this.myFlow.error("Failed to read product: " + productEntry.id, error);
                    splashStore.setFailed(productEntry.id);
                    resolve({
                        id: productEntry.id,
                        name: productEntry.name,
                        manifest: undefined,
                        broken: true,
                        inactiveReason: undefined,
                        external: productEntry.external
                    });
                });
        });
    };

    private fixManifestPaths = (manifest: ProductConfigManifestEx, productEntry: ProductEntry) => {
        if (!manifest.customOptionsComponent) {
            return;
        }

        if (manifest.customOptionsComponent.bundle) {
            // If bundle then view/viewmodel are loaded from memory, only need to fix bundle path
            if (!manifest.customOptionsComponent.bundle.includes(productEntry.configurationProvider)) {
                manifest.customOptionsComponent.bundle =
                    productEntry.configurationProvider + manifest.customOptionsComponent.bundle;
            }

            // RequireJS needs extension if we use absolute path, which we need to do
            // since we haven't setup the script path aliases this early.
            if (!manifest.customOptionsComponent.bundle.endsWith(".js")) {
                manifest.customOptionsComponent.bundle += ".js";
            }
        } else {
            if (manifest.customOptionsComponent.viewModelId) {
                if (!manifest.customOptionsComponent.viewModelId.includes(productEntry.configurationProvider)) {
                    manifest.customOptionsComponent.viewModelId =
                        productEntry.configurationProvider + manifest.customOptionsComponent.viewModelId;
                }

                // RequireJS needs extension if we use absolute path, which we need to do
                // since we haven't setup the script path aliases this early.
                if (!manifest.customOptionsComponent.viewModelId.endsWith(".js")) {
                    manifest.customOptionsComponent.viewModelId += ".js";
                }
            }

            if (manifest.customOptionsComponent.viewUrl) {
                // View could be either rendered page or file that we get through !text
                // Need slightly different logic to prepend with web module url
                if (!manifest.customOptionsComponent.viewUrl.includes(productEntry.configurationProvider)) {
                    if (manifest.customOptionsComponent.viewUrl.startsWith("text!")) {
                        manifest.customOptionsComponent.viewUrl =
                            "text!" +
                            productEntry.configurationProvider +
                            manifest.customOptionsComponent.viewUrl.slice(5);
                    } else {
                        manifest.customOptionsComponent.viewUrl =
                            productEntry.configurationProvider + manifest.customOptionsComponent.viewUrl;
                    }
                }
            }
        }
    };

    private setCacheBusters = (manifest: ProductConfigManifestEx, cacheBuster: string) => {
        if (!manifest.customOptionsComponent || !cacheBuster) {
            return;
        }

        if (manifest.customOptionsComponent.bundle) {
            moduleLoader.setCacheBuster(manifest.customOptionsComponent.bundle, cacheBuster);
        }

        if (manifest.customOptionsComponent.viewModelId) {
            moduleLoader.setCacheBuster(manifest.customOptionsComponent.viewModelId, cacheBuster);
        }

        if (manifest.customOptionsComponent.viewUrl) {
            let viewUrl = manifest.customOptionsComponent.viewUrl;

            // text! loader prefix is not used when matching cache buster, so shouldn't include
            if (viewUrl.startsWith("text!")) {
                viewUrl = viewUrl.slice(5);

                // text! loader also will leave out .html for some reason
                if (viewUrl.endsWith(".html")) {
                    viewUrl = viewUrl.slice(0, viewUrl.length - 5);
                }
            }

            moduleLoader.setCacheBuster(viewUrl, cacheBuster);
        }
    };

    private saveSelection = (selection: ProductSelection): JQueryPromise<void> => {
        if (selection.name) {
            environment.set(environment.keys.userName, selection.name);
        }

        environment.set(environment.keys.productId, selection.id);
        environment.set(environment.keys.productPayload, JSON.stringify(selection.payload));
        environment.set(environment.keys.description, selection.description);
        environment.set(environment.keys.impersonating, selection.impersonating);

        saveProductSelectionToSessionStorage(selection);

        if (selection.hideChange) {
            environment.set(environment.keys.showProductSelector, false);
        }

        const productEntry = this.productEntries.find(pe => pe.id === selection.id);
        if (productEntry?.name && selection.id !== config.otherProductId) {
            document.title = productEntry.name;
        }

        // If selection came from dialog and user checked remember, then save to user settings
        if (selection.remember) {
            const selectedProduct: SelectedProduct = {
                id: selection.id,
                payload: JSON.stringify(selection.payload)
            };

            return service
                .saveSelectedProduct(this.myFlow, selectedProduct)
                .then(() => {
                    environment.set(environment.keys.productIdRemember, selectedProduct.id);
                    environment.set(environment.keys.productPayloadRemember, selectedProduct.payload);
                })
                .catch(() => {
                    this.myFlow.notifyWarningSystem(
                        "Failed to remember selected choice past this session.",
                        "Failed to store selection"
                    );
                });
        }
        return this.myFlow.deferred<void>().resolve().promise();
    };

    reselect = async () => {
        let selectedProduct = this.getSelectedProductFromEnvironment();

        let cancelled = false;

        while (!cancelled) {
            // Always show dialog
            selectedProduct = await this.askUserToSelectProduct(selectedProduct, true, true);
            cancelled = !selectedProduct;

            if (cancelled) {
                return;
            }

            selectedProduct = await this.validateAndSanitizeSelection(selectedProduct);

            if (selectedProduct?.valid) {
                const isClean = this.myFlow.canLeaveSweepWithoutUserFeedback(undefined, false);
                if (!isClean) {
                    const result = await this.myFlow.showConfirmDialog(
                        this.myFlow.translate("FlowHtml.Translations.YouHaveUnsavedDataTitle"),
                        this.myFlow.translate("FlowHtml.Translations.YouHaveUnsavedData"),
                        this.myFlow.translate("FlowHtml.Translations.Abort"),
                        this.myFlow.translate("FlowHtml.Translations.Continue")
                    );

                    // False will mean continue as it is the right side option in dialog
                    if (result === true) {
                        return;
                    }
                }

                if (jumpToFlow2ProductIfNeeded(selectedProduct)) {
                    return;
                }

                await this.saveSelection(selectedProduct);
                this.myFlow.refreshFlow(true);
                return;
            }
        }
    };
}

function jumpToFlow2ProductIfNeeded(product: ProductSelection): boolean {
    if (environment.get<boolean>(environment.keys.crossLaunch) && product.external) {
        const cleanProductId = removeFlow3SuffixFromProductId(product.id);
        const url = flow3NavigationHelper.generateFlow3Url(cleanProductId, undefined, "");
        // Avoid a late dirty check when it is too late to revert
        environment.set(environment.keys.ignorePageUnloadDirtyCheck, true);
        window.location.href = url;
        return true;
    }
    return false;
}
